import { useState, useEffect, useRef } from 'react';
import { Table, Button, Tag, message, Modal, Descriptions } from 'antd';
import { EyeOutlined, FilePdfOutlined } from '@ant-design/icons';
import API from '../api/client';
import { BACKEND_URL } from '../../config';

const eduLabels = { eduPrimary: 'İlköğretim', eduHighSchool: 'Lise', eduAssociate: 'Ön Lisans', eduBachelor: 'Lisans', eduMaster: 'Y. Lisans', eduPhd: 'Doktora' };

const JobApplicationsPage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const printRef = useRef(null);

  const load = async () => {
    try {
      const res = await API.get('/job-applications');
      setData(res.data);
    } catch { message.error('Veriler yüklenemedi'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const markRead = async (id) => {
    await API.put(`/job-applications/${id}/read`);
    load();
  };

  const openDetail = (record) => {
    setDetail(record);
    if (!record.read) markRead(record._id);
  };

  const handlePrintPdf = () => {
    const content = printRef.current;
    if (!content) return;
    const win = window.open('', '_blank');
    win.document.write(`
      <html><head><title>${detail.firstName} ${detail.lastName} - İş Başvurusu</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 12px; color: #333; padding: 20px; }
        .header { text-align: center; margin-bottom: 20px; padding-bottom: 14px; border-bottom: 2px solid #2F2386; }
        .header h1 { font-size: 18px; color: #2F2386; }
        .header p { font-size: 11px; color: #888; margin-top: 4px; }
        .photo-section { display: flex; gap: 16px; margin-bottom: 16px; }
        .photo { width: 90px; height: 115px; object-fit: cover; border-radius: 6px; border: 1px solid #ddd; }
        .no-photo { width: 90px; height: 115px; border: 1px dashed #ccc; border-radius: 6px; display: flex; align-items: center; justify-content: center; color: #ccc; font-size: 10px; }
        .section { margin-bottom: 14px; }
        .section-title { font-size: 13px; font-weight: 700; color: #2F2386; margin-bottom: 6px; padding-bottom: 4px; border-bottom: 1px solid #eee; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 8px; }
        td, th { padding: 4px 8px; border: 1px solid #e0e0e0; font-size: 11px; text-align: left; }
        th { background: #f5f3ff; font-weight: 600; color: #2F2386; }
        .row { display: flex; gap: 12px; margin-bottom: 4px; }
        .row .label { font-weight: 600; min-width: 120px; color: #555; }
        .exp-card { background: #fafafa; padding: 6px 10px; border-radius: 4px; margin-bottom: 4px; }
        @media print { body { padding: 10px; } }
      </style></head><body>
      <div class="header">
        <h1>İŞ TALEP VE BİLGİ FORMU</h1>
        <p>Başvuru Tarihi: ${detail.applicationDate || '-'} | Pozisyon: ${detail.appliedPosition || '-'}</p>
      </div>
      ${content.innerHTML}
      </body></html>
    `);
    win.document.close();
    setTimeout(() => { win.print(); }, 500);
  };

  const columns = [
    {
      title: 'Ad Soyad', key: 'name',
      render: (_, r) => <span style={{ fontWeight: r.read ? 400 : 700 }}>{r.firstName} {r.lastName}</span>,
    },
    { title: 'Pozisyon', dataIndex: 'appliedPosition', key: 'pos' },
    { title: 'Telefon', dataIndex: 'phoneMobile1', key: 'phone' },
    {
      title: 'Durum', dataIndex: 'read', key: 'read',
      render: (read) => read ? <Tag>Okundu</Tag> : <Tag color="red">Yeni</Tag>,
    },
    {
      title: 'Tarih', dataIndex: 'createdAt', key: 'date',
      render: (d) => d ? new Date(d).toLocaleDateString('tr-TR') : '-',
    },
    {
      title: 'İşlem', key: 'action',
      render: (_, record) => (
        <Button size="small" icon={<EyeOutlined />} onClick={() => openDetail(record)}>Detay</Button>
      ),
    },
  ];

  const photoSrc = detail?.photo ? `${BACKEND_URL}${detail.photo}` : null;

  return (
    <>
      <h2 style={{ marginBottom: 20 }}>İş Başvuruları</h2>
      <Table columns={columns} dataSource={data} rowKey="_id" loading={loading} scroll={{ x: 700 }} />

      <Modal
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingRight: 24 }}>
            <span>{detail ? `${detail.firstName} ${detail.lastName} - İş Başvurusu` : ''}</span>
            {detail && (
              <Button icon={<FilePdfOutlined />} onClick={handlePrintPdf} size="small" type="primary">
                PDF İndir
              </Button>
            )}
          </div>
        }
        open={!!detail}
        onCancel={() => setDetail(null)}
        footer={null}
        width={850}
        styles={{ body: { maxHeight: '75vh', overflow: 'auto' } }}
      >
        {detail && (
          <div ref={printRef}>
            {/* A. Kişisel Bilgiler */}
            <div className="section" style={{ marginBottom: 16 }}>
              <h4 style={{ fontSize: 14, fontWeight: 700, color: '#2F2386', marginBottom: 10, paddingBottom: 6, borderBottom: '1px solid #eee' }}>A. Kişisel Bilgiler</h4>
              <div style={{ display: 'flex', gap: 16 }}>
                {photoSrc ? (
                  <img src={photoSrc} alt="Fotoğraf" style={{ width: 100, height: 130, objectFit: 'cover', borderRadius: 8, border: '1px solid #eee', flexShrink: 0 }} />
                ) : (
                  <div style={{ width: 100, height: 130, border: '1px dashed #ddd', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ccc', fontSize: 12, flexShrink: 0 }}>Fotoğraf Yok</div>
                )}
                <Descriptions column={2} size="small" bordered style={{ flex: 1 }}>
                  <Descriptions.Item label="Ad">{detail.firstName}</Descriptions.Item>
                  <Descriptions.Item label="Soyad">{detail.lastName}</Descriptions.Item>
                  <Descriptions.Item label="Pozisyon">{detail.appliedPosition}</Descriptions.Item>
                  <Descriptions.Item label="Başvuru Tarihi">{detail.applicationDate}</Descriptions.Item>
                  <Descriptions.Item label="Cinsiyet">{detail.gender === 'kadin' ? 'Kadın' : detail.gender === 'erkek' ? 'Erkek' : '-'}</Descriptions.Item>
                  <Descriptions.Item label="Doğum Yeri">{detail.birthPlace || '-'}</Descriptions.Item>
                  <Descriptions.Item label="Doğum Tarihi">{detail.birthDate || '-'}</Descriptions.Item>
                  <Descriptions.Item label="Uyruk">{detail.nationality === 'TC' ? 'TC' : detail.nationalityOther || '-'}</Descriptions.Item>
                </Descriptions>
              </div>
              <Descriptions column={2} size="small" bordered style={{ marginTop: 10 }}>
                <Descriptions.Item label="Telefon" span={2}>{[detail.phoneMobile1, detail.phoneMobile2, detail.phoneHome].filter(Boolean).join(' / ') || '-'}</Descriptions.Item>
                <Descriptions.Item label="E-posta" span={2}>{detail.email || '-'}</Descriptions.Item>
                <Descriptions.Item label="Adres" span={2}>{detail.address || '-'}</Descriptions.Item>
                <Descriptions.Item label="Askerlik">{detail.militaryStatus || '-'}{detail.militaryDate ? ` (${detail.militaryDate})` : ''}{detail.militaryExemptReason ? ` - ${detail.militaryExemptReason}` : ''}</Descriptions.Item>
                <Descriptions.Item label="Ehliyet">{detail.driverLicense?.join(', ') || '-'}</Descriptions.Item>
                <Descriptions.Item label="Medeni Durum">{detail.maritalStatus || '-'}{detail.spouseJob ? ` (Eş: ${detail.spouseJob})` : ''}</Descriptions.Item>
                <Descriptions.Item label="Sigara / Çocuk">{detail.smoking === 'evet' ? 'Evet' : detail.smoking === 'hayir' ? 'Hayır' : '-'} / {detail.childCount || '0'}</Descriptions.Item>
              </Descriptions>
            </div>

            {/* B. Eğitim */}
            <div style={{ marginBottom: 16 }}>
              <h4 style={{ fontSize: 14, fontWeight: 700, color: '#2F2386', marginBottom: 8, paddingBottom: 6, borderBottom: '1px solid #eee' }}>B. Eğitim Bilgileri</h4>
              <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                <thead><tr style={{ background: '#f5f3ff' }}>
                  <th style={{ padding: 6, textAlign: 'left', borderBottom: '1px solid #ddd' }}>Seviye</th>
                  <th style={{ padding: 6, textAlign: 'left', borderBottom: '1px solid #ddd' }}>Okul</th>
                  <th style={{ padding: 6, textAlign: 'left', borderBottom: '1px solid #ddd' }}>Bölüm</th>
                  <th style={{ padding: 6, textAlign: 'left', borderBottom: '1px solid #ddd' }}>Başlangıç</th>
                  <th style={{ padding: 6, textAlign: 'left', borderBottom: '1px solid #ddd' }}>Bitiş</th>
                  <th style={{ padding: 6, textAlign: 'left', borderBottom: '1px solid #ddd' }}>Derece</th>
                </tr></thead>
                <tbody>
                  {Object.entries(eduLabels).map(([key, label]) => {
                    const edu = detail[key];
                    if (!edu?.school) return null;
                    return (
                      <tr key={key}>
                        <td style={{ padding: 6, borderBottom: '1px solid #f0f0f0', fontWeight: 600 }}>{label}</td>
                        <td style={{ padding: 6, borderBottom: '1px solid #f0f0f0' }}>{edu.school}</td>
                        <td style={{ padding: 6, borderBottom: '1px solid #f0f0f0' }}>{edu.department}</td>
                        <td style={{ padding: 6, borderBottom: '1px solid #f0f0f0' }}>{edu.startDate}</td>
                        <td style={{ padding: 6, borderBottom: '1px solid #f0f0f0' }}>{edu.endDate}</td>
                        <td style={{ padding: 6, borderBottom: '1px solid #f0f0f0' }}>{edu.grade}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* C. Yabancı Dil */}
            {detail.languages?.some(l => l.language) && (
              <div style={{ marginBottom: 16 }}>
                <h4 style={{ fontSize: 14, fontWeight: 700, color: '#2F2386', marginBottom: 8, paddingBottom: 6, borderBottom: '1px solid #eee' }}>C. Yabancı Dil</h4>
                <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                  <thead><tr style={{ background: '#f5f3ff' }}>
                    <th style={{ padding: 6, textAlign: 'left', borderBottom: '1px solid #ddd' }}>Dil</th>
                    <th style={{ padding: 6, textAlign: 'left', borderBottom: '1px solid #ddd' }}>Okuma</th>
                    <th style={{ padding: 6, textAlign: 'left', borderBottom: '1px solid #ddd' }}>Yazma</th>
                    <th style={{ padding: 6, textAlign: 'left', borderBottom: '1px solid #ddd' }}>Konuşma</th>
                  </tr></thead>
                  <tbody>
                    {detail.languages.filter(l => l.language).map((l, i) => (
                      <tr key={i}>
                        <td style={{ padding: 6, borderBottom: '1px solid #f0f0f0', fontWeight: 600 }}>{l.language}</td>
                        <td style={{ padding: 6, borderBottom: '1px solid #f0f0f0' }}>{l.readLevel || '-'}</td>
                        <td style={{ padding: 6, borderBottom: '1px solid #f0f0f0' }}>{l.writeLevel || '-'}</td>
                        <td style={{ padding: 6, borderBottom: '1px solid #f0f0f0' }}>{l.speakLevel || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* D. Bilgisayar */}
            {(detail.computerExcel || detail.computerWord || detail.computerPowerPoint) && (
              <div style={{ marginBottom: 16 }}>
                <h4 style={{ fontSize: 14, fontWeight: 700, color: '#2F2386', marginBottom: 8, paddingBottom: 6, borderBottom: '1px solid #eee' }}>D. Bilgisayar Bilgisi</h4>
                <Descriptions column={3} size="small" bordered>
                  <Descriptions.Item label="Excel">{detail.computerExcel || '-'}</Descriptions.Item>
                  <Descriptions.Item label="Word">{detail.computerWord || '-'}</Descriptions.Item>
                  <Descriptions.Item label="PowerPoint">{detail.computerPowerPoint || '-'}</Descriptions.Item>
                </Descriptions>
              </div>
            )}

            {/* E. İş Tecrübesi */}
            {detail.experiences?.some(e => e.company) && (
              <div style={{ marginBottom: 16 }}>
                <h4 style={{ fontSize: 14, fontWeight: 700, color: '#2F2386', marginBottom: 8, paddingBottom: 6, borderBottom: '1px solid #eee' }}>E. İş Tecrübesi</h4>
                <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                  <thead><tr style={{ background: '#f5f3ff' }}>
                    <th style={{ padding: 6, textAlign: 'left', borderBottom: '1px solid #ddd' }}>Firma</th>
                    <th style={{ padding: 6, textAlign: 'left', borderBottom: '1px solid #ddd' }}>Görev</th>
                    <th style={{ padding: 6, textAlign: 'left', borderBottom: '1px solid #ddd' }}>Giriş</th>
                    <th style={{ padding: 6, textAlign: 'left', borderBottom: '1px solid #ddd' }}>Çıkış</th>
                    <th style={{ padding: 6, textAlign: 'left', borderBottom: '1px solid #ddd' }}>Ayrılma Sebebi</th>
                    <th style={{ padding: 6, textAlign: 'left', borderBottom: '1px solid #ddd' }}>Ücret</th>
                  </tr></thead>
                  <tbody>
                    {detail.experiences.filter(e => e.company).map((exp, i) => (
                      <tr key={i}>
                        <td style={{ padding: 6, borderBottom: '1px solid #f0f0f0' }}>{exp.company}</td>
                        <td style={{ padding: 6, borderBottom: '1px solid #f0f0f0' }}>{exp.position}</td>
                        <td style={{ padding: 6, borderBottom: '1px solid #f0f0f0' }}>{exp.startDate}</td>
                        <td style={{ padding: 6, borderBottom: '1px solid #f0f0f0' }}>{exp.endDate || 'Devam'}</td>
                        <td style={{ padding: 6, borderBottom: '1px solid #f0f0f0' }}>{exp.leaveReason || '-'}</td>
                        <td style={{ padding: 6, borderBottom: '1px solid #f0f0f0' }}>{exp.salary || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* F. Kurs/Sertifika */}
            {detail.courses?.some(c => c.topic) && (
              <div style={{ marginBottom: 16 }}>
                <h4 style={{ fontSize: 14, fontWeight: 700, color: '#2F2386', marginBottom: 8, paddingBottom: 6, borderBottom: '1px solid #eee' }}>F. Kurs / Sertifika / Ödüller</h4>
                <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                  <thead><tr style={{ background: '#f5f3ff' }}>
                    <th style={{ padding: 6, textAlign: 'left', borderBottom: '1px solid #ddd' }}>Konu</th>
                    <th style={{ padding: 6, textAlign: 'left', borderBottom: '1px solid #ddd' }}>Kurum</th>
                    <th style={{ padding: 6, textAlign: 'left', borderBottom: '1px solid #ddd' }}>Süre</th>
                    <th style={{ padding: 6, textAlign: 'left', borderBottom: '1px solid #ddd' }}>Tarih</th>
                  </tr></thead>
                  <tbody>
                    {detail.courses.filter(c => c.topic).map((c, i) => (
                      <tr key={i}>
                        <td style={{ padding: 6, borderBottom: '1px solid #f0f0f0' }}>{c.topic}</td>
                        <td style={{ padding: 6, borderBottom: '1px solid #f0f0f0' }}>{c.institution}</td>
                        <td style={{ padding: 6, borderBottom: '1px solid #f0f0f0' }}>{c.duration}</td>
                        <td style={{ padding: 6, borderBottom: '1px solid #f0f0f0' }}>{c.date}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* G-H */}
            {(detail.memberships || detail.hobbies) && (
              <div style={{ marginBottom: 16 }}>
                <h4 style={{ fontSize: 14, fontWeight: 700, color: '#2F2386', marginBottom: 8, paddingBottom: 6, borderBottom: '1px solid #eee' }}>G-H. Üyelikler & Hobiler</h4>
                <Descriptions column={1} size="small" bordered>
                  {detail.memberships && <Descriptions.Item label="Üyelikler">{detail.memberships}</Descriptions.Item>}
                  {detail.hobbies && <Descriptions.Item label="Hobiler">{detail.hobbies}</Descriptions.Item>}
                </Descriptions>
              </div>
            )}

            {/* I. Diğer */}
            <div style={{ marginBottom: 16 }}>
              <h4 style={{ fontSize: 14, fontWeight: 700, color: '#2F2386', marginBottom: 8, paddingBottom: 6, borderBottom: '1px solid #eee' }}>I. Diğer Bilgiler</h4>
              <Descriptions column={2} size="small" bordered>
                <Descriptions.Item label="Sağlık Problemi">{detail.healthIssue === 'evet' ? `Evet: ${detail.healthDetail}` : 'Hayır'}</Descriptions.Item>
                <Descriptions.Item label="Mahkumiyet">{detail.criminalRecord === 'evet' ? `Evet: ${detail.criminalDetail}` : 'Hayır'}</Descriptions.Item>
                <Descriptions.Item label="Acil Durum Kişisi">{detail.emergencyName || '-'}</Descriptions.Item>
                <Descriptions.Item label="Yakınlığı / Tel">{detail.emergencyRelation || '-'} / {detail.emergencyPhone || '-'}</Descriptions.Item>
              </Descriptions>
            </div>

            {/* J. Referanslar */}
            {detail.references?.some(r => r.name) && (
              <div style={{ marginBottom: 16 }}>
                <h4 style={{ fontSize: 14, fontWeight: 700, color: '#2F2386', marginBottom: 8, paddingBottom: 6, borderBottom: '1px solid #eee' }}>J. Referanslar</h4>
                <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                  <thead><tr style={{ background: '#f5f3ff' }}>
                    <th style={{ padding: 6, textAlign: 'left', borderBottom: '1px solid #ddd' }}>Ad Soyad</th>
                    <th style={{ padding: 6, textAlign: 'left', borderBottom: '1px solid #ddd' }}>Kurum</th>
                    <th style={{ padding: 6, textAlign: 'left', borderBottom: '1px solid #ddd' }}>Görev</th>
                    <th style={{ padding: 6, textAlign: 'left', borderBottom: '1px solid #ddd' }}>Telefon</th>
                  </tr></thead>
                  <tbody>
                    {detail.references.filter(r => r.name).map((r, i) => (
                      <tr key={i}>
                        <td style={{ padding: 6, borderBottom: '1px solid #f0f0f0' }}>{r.name}</td>
                        <td style={{ padding: 6, borderBottom: '1px solid #f0f0f0' }}>{r.company}</td>
                        <td style={{ padding: 6, borderBottom: '1px solid #f0f0f0' }}>{r.position}</td>
                        <td style={{ padding: 6, borderBottom: '1px solid #f0f0f0' }}>{r.phone}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* K-L */}
            <div style={{ marginBottom: 16 }}>
              <h4 style={{ fontSize: 14, fontWeight: 700, color: '#2F2386', marginBottom: 8, paddingBottom: 6, borderBottom: '1px solid #eee' }}>K-L. Ücret & Ek Bilgiler</h4>
              <Descriptions column={2} size="small" bordered>
                <Descriptions.Item label="Ücret Beklentisi (Brüt)">{detail.salaryExpGross || '-'}</Descriptions.Item>
                <Descriptions.Item label="Ücret Beklentisi (Net)">{detail.salaryExpNet || '-'}</Descriptions.Item>
                <Descriptions.Item label="Üst Beden">{detail.sizeTop || '-'}</Descriptions.Item>
                <Descriptions.Item label="Alt Beden">{detail.sizeBottom || '-'}</Descriptions.Item>
                <Descriptions.Item label="Ayakkabı No">{detail.shoeSize || '-'}</Descriptions.Item>
                <Descriptions.Item label="Vardiyalı Çalışma">{detail.shiftWork === 'evet' ? 'Evet' : detail.shiftWork === 'hayir' ? 'Hayır' : '-'}</Descriptions.Item>
              </Descriptions>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default JobApplicationsPage;
